import React, { useState } from 'react'
import { Dropdown, Menu, Spin, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useCountDown, useRequest } from 'ahooks'
import Button from '@/components/button/button'
import Iconfont from '@/components/iconfont/iconfont'
import SgInput from '@/components/input/input'
import SearchInput from '@/components/search-input/search-input'
import { Column, Row } from '@/components/layout/layout'
import $msg from '@/utils/message'
import { getLsMembers, importLsMembers, requestLsShopValidation, searchLsShop, validateLsShop } from '../api'
import { memberManageModel } from '../model'
import { ImportStepBar, ImportTips } from './import-layout'

interface Props {
    onImportStarted?: () => void
    importing?: boolean
}

const ShopImport: React.FC<Props> = props => {
    const importTips = [
        '仅限从森果老零售店铺导入会员，可迁移会员姓名、手机号、余额、积分和会员卡号。',
        '店铺超管验证通过后会先预览会员数据，确认后再开始后台导入。',
    ]
    const importSteps = [{ title: '输入店铺号' }, { title: '超管验证' }]
    const [shopInfo, setShopInfo] = useState<any>(null)
    const [shopCode, setShopCode] = useState('')
    const [validationCode, setValidationCode] = useState('')
    const [currentStep, setCurrentStep] = useState(0)
    const [validated, setValidated] = useState(false)
    const [importStarted, setImportStarted] = useState(false)
    const [sendingCode, setSendingCode] = useState(false)
    const [validating, setValidating] = useState(false)
    const [codeError, setCodeError] = useState(false)
    const [codeSent, setCodeSent] = useState(false)
    const [countdownStatus, setCountdownStatus] = useState(0)
    const [countdown, setTargetDate] = useCountDown({
        onEnd: () => setCountdownStatus(2),
    })
    const searchReq = useRequest(searchLsShop, {
        manual: true,
        onSuccess: res => {
            setShopInfo(res.shop_info || null)
            setValidated(false)
            setImportStarted(false)
            setCurrentStep(0)
            setValidationCode('')
            setCodeError(false)
            setCodeSent(false)
            setCountdownStatus(0)
            if (!res.shop_info) $msg('warning', '未找到老零售店铺')
        },
        onError: e => $msg('error', e.message || '搜索老零售店铺失败'),
    })
    const memberReq = useRequest(() => getLsMembers({ ls_shop_id: shopInfo?.old_ls_shop_id, page: 1 }), {
        manual: true,
        onError: e => $msg('error', e.message || '获取老零售会员失败'),
    })
    const importReq = useRequest(importLsMembers, {
        manual: true,
        onSuccess: () => {
            memberManageModel.setImportingMemberData(true)
            setImportStarted(true)
            $msg('success', '已开始导入')
            props.onImportStarted && props.onImportStarted()
        },
        onError: e => $msg('error', e.message || '导入失败'),
    })

    const onSearch = (value: string) => {
        const nextShopCode = value.trim()
        if (!nextShopCode) {
            $msg('warning', '请输入老零售店铺号')

            return
        }
        setShopCode(nextShopCode)
        searchReq.run({ shop_code: nextShopCode })
    }

    const onSendCode = async () => {
        if (!shopInfo) return
        if (countdownStatus === 1) return
        setSendingCode(true)
        try {
            await requestLsShopValidation({ ls_shop_id: Number(shopInfo.old_ls_shop_id) })
            setCodeSent(true)
            setTargetDate(Date.now() + 60 * 1000)
            setCountdownStatus(1)
            $msg('success', '验证码已发送')
        } catch (e) {
            $msg('error', e.message || '发送验证码失败')
        } finally {
            setSendingCode(false)
        }
    }

    const onValidate = async () => {
        if (!shopInfo) return
        if (!validationCode) {
            $msg('warning', '请输入验证码')

            return
        }
        setValidating(true)
        try {
            const res = await validateLsShop({
                ls_shop_id: Number(shopInfo.old_ls_shop_id),
                validation_code: validationCode,
            })
            if (!res.success) {
                setCodeError(true)
                $msg('error', '验证码错误')

                return
            }
            setCodeError(false)
            setValidated(true)
            setImportStarted(false)
            setCountdownStatus(2)
            $msg('success', '验证成功')
            memberReq.run()
        } catch (e) {
            $msg('error', e.message || '验证失败')
        } finally {
            setValidating(false)
        }
    }

    const columns: ColumnsType<any> = [
        { title: '会员姓名', dataIndex: 'realname', width: 140, render: value => value || '-' },
        { title: '昵称', dataIndex: 'nickname', width: 140, render: value => value || '-' },
        { title: '手机号', dataIndex: 'phone', width: 152, render: value => value || '-' },
        { title: '生日', dataIndex: 'birthday', width: 120, render: value => value || '-' },
        { title: '余额', dataIndex: 'balance', width: 112 },
        { title: '积分', dataIndex: 'point', width: 96 },
        { title: '会员卡号', dataIndex: 'card_code', width: 160, render: value => value || '-' },
    ]
    const previewMembers = memberReq.data?.member_infos || []
    const canImport = validated && !memberReq.loading && !importStarted && !props.importing && previewMembers.length > 0
    const hasShopInfo = !!shopInfo

    return (
        <Column className="member-import__panel" data-align-items-center>
            <ImportTips tips={importTips} className="member-import__tips" />
            <ImportStepBar steps={importSteps} current={currentStep} />
            {currentStep === 0 ? (
                <div className="member-import__search">
                    <SearchInput
                        isExpaned
                        searchDom={
                            <Dropdown
                                overlay={
                                    <Menu>
                                        <Menu.Item disabled={!hasShopInfo}>
                                            <Spin spinning={searchReq.loading}>
                                                <div
                                                    className="member-import__search-item"
                                                    onClick={() => {
                                                        if (hasShopInfo) setCurrentStep(1)
                                                    }}
                                                >
                                                    {hasShopInfo ? (
                                                        <>
                                                            <Row data-align-items-center>
                                                                <div className="member-import__shop-tag">门店零售</div>
                                                                <div className="member-import__shop-title">
                                                                    {shopInfo.shop_name}
                                                                </div>
                                                            </Row>
                                                            <div className="action">选择</div>
                                                        </>
                                                    ) : (
                                                        '暂无搜索结果'
                                                    )}
                                                </div>
                                            </Spin>
                                        </Menu.Item>
                                    </Menu>
                                }
                                trigger={['click']}
                            >
                                <div className="member-import__search-action" />
                            </Dropdown>
                        }
                        placeholder="请输入老零售店铺号"
                        onSearch={onSearch}
                        onDelete={() => {
                            setShopInfo(null)
                            setShopCode('')
                        }}
                    />
                    <Iconfont name="icon8yiwen-16" size={24} />
                </div>
            ) : null}
            {currentStep === 1 && shopInfo ? (
                <Column data-width-100p data-align-items-center>
                    <div className="member-import__validate-title">{shopInfo.shop_name}</div>
                    <div className="member-import__validate-desc">店铺号: {shopCode}</div>
                    <Row className="member-import__validate-card" data-align-items-center>
                        <div className="member-import__user">
                            {shopInfo.super_admin_imgurl ? (
                                <img src={shopInfo.super_admin_imgurl} className="member-import__avatar" alt="" />
                            ) : (
                                <div className="member-import__avatar">
                                    {(shopInfo.super_admin_nickname || shopInfo.super_admin_phone || '-').slice(0, 1)}
                                </div>
                            )}
                            <div className="member-import__user-info">
                                <span className="member-import__user-name">{shopInfo.super_admin_nickname || '-'}</span>
                                <span>{shopInfo.super_admin_phone || '-'}</span>
                            </div>
                        </div>
                        {!codeSent ? (
                            <Button types="default" loading={sendingCode} onClick={onSendCode}>
                                发送验证码
                            </Button>
                        ) : (
                            <div className="member-import__verify-input">
                                {codeError ? (
                                    <div className="member-import__verify-error">验证码错误，请重新输入</div>
                                ) : null}
                                <SgInput
                                    value={validationCode}
                                    error={codeError}
                                    maxLength={6}
                                    onChange={e => {
                                        setCodeError(false)
                                        setValidationCode(e.currentTarget.value)
                                    }}
                                    inputsuffix={() => (
                                        <div
                                            className={`member-import__verify-suffix ${
                                                countdownStatus === 1 ? 'member-import__verify-suffix--disabled' : ''
                                            }`}
                                            onClick={onSendCode}
                                        >
                                            {countdownStatus === 1
                                                ? `已发送(${Math.round(countdown / 1000)})`
                                                : '重新发送'}
                                        </div>
                                    )}
                                />
                            </div>
                        )}
                    </Row>
                    <Row data-margin-top-32>
                        <Button
                            types="text"
                            className="member-import__back-button"
                            onClick={() => {
                                setCurrentStep(0)
                                setValidated(false)
                                setImportStarted(false)
                                setValidationCode('')
                                setCodeError(false)
                            }}
                        >
                            上一步
                        </Button>
                        <Button
                            types="default"
                            loading={validating}
                            disabled={!codeSent || validationCode.length < 4}
                            onClick={onValidate}
                        >
                            验证并预览
                        </Button>
                    </Row>
                </Column>
            ) : null}
            {validated && (
                <div className="member-import__preview">
                    <Row className="member-import__toolbar" data-align-items-center>
                        <span>共 {memberReq.data?.member_count || 0} 个会员</span>
                        <Button
                            types="default"
                            loading={importReq.loading || memberReq.loading}
                            disabled={!canImport}
                            onClick={() =>
                                importReq.run({
                                    ls_shop_id: Number(shopInfo.old_ls_shop_id),
                                    ls_shop_name: shopInfo.shop_name,
                                })
                            }
                        >
                            {importStarted ? '已开始导入' : '开始导入'}
                        </Button>
                    </Row>
                    <div className="table-container member-import__preview-table">
                        <Table
                            bordered
                            rowKey={(record, index) => `${record.phone}-${index}`}
                            loading={memberReq.loading}
                            columns={columns}
                            dataSource={previewMembers}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                        />
                    </div>
                </div>
            )}
        </Column>
    )
}

export default ShopImport
